import api from './api'

export const managedDevicesApi = {
  // List managed devices (unified routers + hosts)
  getAll: async () => {
    const response = await api.get('/managed-devices')
    return response.data
  },

  // List managed devices by tenant with optional filtering
  getByTenant: async (tenantId, kind = null) => {
    const params = kind ? { kind } : {}
    const response = await api.get(`/tenants/${tenantId}/managed-devices`, { params })
    return response.data
  },

  // Get managed device by ID
  getById: async (id) => {
    const response = await api.get(`/managed-devices/${id}`)
    return response.data
  },

  // Get device by fingerprint (for device identification)
  getByFingerprint: async (fingerprint) => {
    const response = await api.get(`/managed-devices/by-fingerprint/${fingerprint}`)
    return response.data
  },

  // Create managed device (router or host)
  create: async (tenantId, data) => {
    const response = await api.post(`/tenants/${tenantId}/managed-devices`, data)
    return response.data
  },

  // Update managed device
  update: async (id, data) => {
    const response = await api.put(`/managed-devices/${id}`, data)
    return response.data
  },

  // Delete managed device
  delete: async (id) => {
    await api.delete(`/managed-devices/${id}`)
  },

  // Test connection (works for both routers and hosts)
  testConnection: async (id) => {
    const response = await api.post(`/managed-devices/${id}/test-connection`)
    return response.data
  },

  // Host-specific: Activate setup for host devices
  activateSetup: async (id) => {
    const response = await api.post(`/managed-devices/${id}/activate-setup`)
    return response.data
  },

  // Host-specific: Get setup script for host installation
  getSetupScript: async (id) => {
    const response = await api.get(`/managed-devices/${id}/setup-script`)
    return response.data
  },

  // Host-specific: Complete setup process
  completeSetup: async (id, completeData) => {
    await api.post(`/managed-devices/${id}/setup-complete`, completeData)
  },

  // Router-specific: Bootstrap activation for routers
  activateBootstrap: async (id) => {
    const response = await api.post(`/managed-devices/${id}/bootstrap-activate`)
    return response.data
  },

  // Router-specific: Get bootstrap script
  getBootstrapScript: async (id) => {
    const response = await api.get(`/managed-devices/${id}/bootstrap-script`)
    return response.data
  },

  // Router-specific: Complete bootstrap process
  completeBootstrap: async (id) => {
    await api.get(`/managed-devices/${id}/bootstrap-complete`)
  },

  // Host-specific: Get remote display credentials (VNC)
  getRemoteDisplayCredentials: async (id) => {
    const response = await api.get(`/managed-devices/${id}/remote-display-credentials`)
    return response.data
  },

  // Host-specific: Get WebRTC credentials for remote connections
  getWebRtcCredentials: async (id) => {
    const response = await api.get(`/managed-devices/${id}/webrtc-credentials`)
    return response.data
  },

  // Update device password
  updatePassword: async (id, passwordData) => {
    await api.put(`/managed-devices/${id}/password`, passwordData)
  },

  // Group management: Move device to group
  moveToGroup: async (id, groupId) => {
    const response = await api.patch(`/managed-devices/${id}/group`, { groupId: groupId ?? null })
    return response.data
  },

  // Update device icon
  updateIcon: async (id, customIconBase64) => {
    const response = await api.put(`/managed-devices/${id}/icon`, { customIconBase64: customIconBase64 ?? null })
    return response.data
  },

  // VPN configuration: Get WireGuard client config
  getVpnClientConfig: async (id) => {
    const response = await api.get(`/managed-devices/${id}/vpn/client-config`)
    return response.data
  },

  // Metrics history (InfluxDB-backed). range = "1h"|"6h"|"24h"|"7d"; fields = optional CSV.
  getMetricsHistory: async (id, { range = '1h', fields = null } = {}) => {
    const params = { range }
    if (fields && fields.length) params.fields = Array.isArray(fields) ? fields.join(',') : fields
    const response = await api.get(`/managed-devices/${id}/metrics/history`, { params })
    return response.data
  },

  // VPN configuration: Download VPN config file
  downloadVpnConfig: async (deviceId) => {
    const response = await api.get(`/managed-devices/${deviceId}/vpn/client-config/download`, {
      responseType: 'blob',
    })

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    let filename = `device_${deviceId}.conf`
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },

  // Device filtering helpers
  getRouters: async (tenantId) => {
    return await managedDevicesApi.getByTenant(tenantId, 'Router')
  },

  getHosts: async (tenantId) => {
    return await managedDevicesApi.getByTenant(tenantId, 'Host')
  },

  // Device type helpers
  isRouter: (device) => {
    return device.kind === 'Router' || device.type === 'router' || device.kind?.includes('Router')
  },

  isHost: (device) => {
    return device.kind === 'Host' || device.type === 'host' || device.kind?.includes('Host') || device.kind?.includes('Linux') || device.kind?.includes('Windows') || device.kind?.includes('Android')
  },

  // Get device capabilities based on type
  getDeviceCapabilities: (device) => {
    const capabilities = {
      hasRouterManagement: false,
      hasTerminalAccess: false,
      hasRemoteDisplay: false,
      hasVpnConfig: false,
      hasBootstrap: false,
      hasSetup: false,
      hasWebRtc: false
    }

    if (managedDevicesApi.isRouter(device)) {
      capabilities.hasRouterManagement = true
      capabilities.hasTerminalAccess = true  // RouterOS terminal
      capabilities.hasVpnConfig = true
      capabilities.hasBootstrap = true
    }

    if (managedDevicesApi.isHost(device)) {
      capabilities.hasTerminalAccess = true  // SSH terminal
      capabilities.hasRemoteDisplay = device.kind?.includes('Linux') || device.kind?.includes('Ubuntu')
      capabilities.hasVpnConfig = true
      capabilities.hasSetup = true
      capabilities.hasWebRtc = true
    }

    return capabilities
  }
}

// Maintain backward compatibility with legacy APIs (temporary)
// These should be removed in Phase 4 cleanup
export const routersApi = {
  getByTenant: (tenantId) => managedDevicesApi.getRouters(tenantId),
  getById: managedDevicesApi.getById,
  create: managedDevicesApi.create,
  update: managedDevicesApi.update,
  delete: managedDevicesApi.delete,
  testConnection: managedDevicesApi.testConnection,
  downloadVpnConfig: managedDevicesApi.downloadVpnConfig,
  activateBootstrap: managedDevicesApi.activateBootstrap,
  moveToGroup: managedDevicesApi.moveToGroup,
  updateIcon: managedDevicesApi.updateIcon,
  // Legacy method - map to new API
  regenerateVpnPeerKeys: async (peerId) => {
    // This needs to be handled differently in unified API
    const response = await api.post(`/vpn/peers/${peerId}/regenerate-keys`)
    return response.data
  }
}

export const hostsApi = {
  getByTenant: (tenantId) => managedDevicesApi.getHosts(tenantId),
  getById: managedDevicesApi.getById,
  create: managedDevicesApi.create,
  update: managedDevicesApi.update,
  delete: managedDevicesApi.delete,
  getRemoteDisplayCredentials: managedDevicesApi.getRemoteDisplayCredentials,
  getWebRtcCredentials: managedDevicesApi.getWebRtcCredentials,
  activateSetup: managedDevicesApi.activateSetup,
  moveToGroup: managedDevicesApi.moveToGroup,
  updateIcon: managedDevicesApi.updateIcon
}